import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CollectionGuide, useCollectionCopy } from '@todo/web-ui/components/collection-guide';
import { HotelsMapFrame } from '@todo/web-ui/components/hotels';
import { ICONS, Icon } from '@todo/web-ui/components/icon';
import { NavLink } from '@todo/web-ui/components/nav-link';
import { Intro, Page } from '@todo/web-ui/components/page';
import { PageHead } from '@todo/web-ui/components/page-head';
import { Section } from '@todo/web-ui/components/section';
import { localePath } from '@todo/web-ui/lib/format';
import { DUBAI_STAY, DUBAI_ZOOM, DUBAI_ZOOM_NARROW, HOTEL_AREAS, type HotelArea } from '@/lib/hotel-areas';
import { STAY22_SOURCE, stay22MapUrl, stay22Url, type Stay } from '@todo/core/hotels';
import type { Locale } from '@todo/core/i18n/locales';
import { useLocale } from '@todo/core/i18n/use-locale';
import { openExternal } from '@todo/core/open-external';
import { breadcrumbJsonLd, faqPageJsonLd, seoTitle } from '@todo/core/seo';
import { SITE } from '@site';

import css from './hotels.module.css';

// "Where to stay in Dubai": Stay22's map of hotels and rentals, a chip per area
// that re-centres it, then a card per area (who it suits, getting around, a
// "See hotels" search and a link to its things-to-do page), and a guide + FAQ
// from `collections.hotels` — the indexable part, since crawlers read nothing
// from the map's iframe. Area copy: `hotelsPage.areas.<id>`; points: src/lib/hotel-areas.ts.
//
// As everywhere on this site, the map loads only on the visitor's click (the
// widget is a full map app with its own analytics): until then a placeholder
// with a button holds its place. Every "See hotels" is an Allez search built on
// click and tracked as an `affiliate-click` (kind 'hotel', id 'hotels');
// clicks inside the map reach only Stay22's reports (`hotels_<area>_map`).

const PATH = 'hotels';
/** Umami `id` of this page's hotel clicks. */
const PAGE_ID = 'hotels';
/** The whole city, in the chips and campaigns. */
const ALL = SITE.stay22.region;

// Below this the whole city needs one zoom level less.
const NARROW_QUERY = '(max-width: 639px)';

function mapSrc(area: HotelArea | null, locale: Locale): string {
  if (!area) {
    const narrow = window.matchMedia(NARROW_QUERY).matches;
    return stay22MapUrl(DUBAI_STAY, {
      campaign: `hotels_${ALL}`,
      locale,
      zoom: narrow ? DUBAI_ZOOM_NARROW : DUBAI_ZOOM,
      hideMainMarker: true,
    });
  }
  // An area's point marks nothing a visitor would look for either.
  return stay22MapUrl(area.stay, { campaign: `hotels_${area.id}`, locale, zoom: area.zoom, hideMainMarker: true });
}

function openHotels(stay: Stay, campaign: string, title: string, locale: Locale) {
  openExternal(stay22Url(stay, { campaign, locale }), {
    kind: 'hotel',
    source: STAY22_SOURCE,
    id: PAGE_ID,
    title,
    city: SITE.cityIds[0],
    locale,
  });
}

export default function HotelsPage() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const copy = useCollectionCopy(PATH);
  const title = t('collections.hotels.title');
  // The area on the map (null: all of Dubai) and the map's URL once opened.
  const [area, setArea] = useState<HotelArea | null>(null);
  const [src, setSrc] = useState<string | null>(null);

  const show = (next: HotelArea | null) => {
    setArea(next);
    setSrc(mapSrc(next, locale));
  };
  const areaName = (a: HotelArea) => t(`hotelsPage.areas.${a.id}.name`);

  return (
    <Page>
      <PageHead title={seoTitle(t('collections.hotels.seoTitle'))} description={t('collections.hotels.meta')} path={PATH} indexable>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd(locale, [{ name: title }]))}</script>
        {copy.faq.length ? <script type="application/ld+json">{JSON.stringify(faqPageJsonLd(copy.faq))}</script> : null}
      </PageHead>

      <Section id="hotels-title" level={1} title={t('collections.hotels.heading')}>
        <Intro>{t('collections.hotels.intro')}</Intro>

        <div className={css.chips} role="group" aria-label={t('hotelsPage.chipsLabel')}>
          {[null, ...HOTEL_AREAS].map((a) => {
            const active = src != null && area?.id === a?.id;
            return (
              <button
                key={a?.id ?? ALL}
                type="button"
                className={css.chip}
                aria-pressed={active}
                onClick={() => show(a)}>
                <Icon d={ICONS.pin} size={14} />
                {a ? areaName(a) : t('hotelsPage.all')}
              </button>
            );
          })}
        </div>

        {src ? (
          <HotelsMapFrame src={src} wide />
        ) : (
          <div className={css.placeholder}>
            <Icon d={ICONS.map} size={36} strokeWidth={1.2} />
            <p>{t('hotelsPage.mapText')}</p>
            <button type="button" className={css.primary} onClick={() => show(null)}>
              {t('hotelsPage.openMap')}
            </button>
          </div>
        )}

        <p className={css.allLine}>
          <button
            type="button"
            className={css.secondary}
            onClick={() => openHotels(DUBAI_STAY, `hotels_${ALL}`, title, locale)}>
            <Icon d={ICONS.bed} size={20} />
            {t('hotelsPage.seeAll')}
          </button>
        </p>
      </Section>

      <Section id="hotels-areas" title={t('hotelsPage.areasTitle')}>
        <div className={css.areas}>
          {HOTEL_AREAS.map((a) => {
            const key = `hotelsPage.areas.${a.id}`;
            const paragraphs = t(`${key}.p`, { returnObjects: true });
            return (
              <article key={a.id} id={`area-${a.id}`} className={css.area}>
                <h3 className={css.areaName}>{areaName(a)}</h3>
                <p className={css.bestFor}>
                  <span>{t('hotelsPage.bestFor')}</span> {t(`${key}.bestFor`)}
                </p>
                {(Array.isArray(paragraphs) ? (paragraphs as string[]) : []).map((text) => (
                  <p key={text} className={css.text}>
                    {text}
                  </p>
                ))}
                <div className={css.actions}>
                  <button
                    type="button"
                    className={css.secondary}
                    onClick={() => openHotels(a.stay, `hotels_${a.id}`, areaName(a), locale)}>
                    <Icon d={ICONS.bed} size={20} />
                    {t(`${key}.cta`)}
                  </button>
                  <button type="button" className={css.link} onClick={() => show(a)}>
                    <Icon d={ICONS.map} size={18} />
                    {t('hotelsPage.showOnMap')}
                  </button>
                  {a.collection ? (
                    <NavLink href={localePath(locale, a.collection)} className={css.link}>
                      {t(`${key}.thingsToDo`)}
                    </NavLink>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      </Section>

      <CollectionGuide slug={PATH} copy={copy} />
    </Page>
  );
}
